import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import React, { useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { FiChevronDown, FiPlus, FiTrash2, FiLayers } from 'react-icons/fi';

// Accordion Item Component
const AccordionItemComponent = ({ item, index, onUpdate, onDelete, isEditing }) => {
  const [isOpen, setIsOpen] = useState(index === 0);

  const handleTitleChange = (e) => {
    onUpdate(index, { ...item, title: e.target.value });
  };

  const handleContentChange = (e) => {
    onUpdate(index, { ...item, content: e.target.value });
  };

  return (
    <div className={`accordion-item ${isOpen ? 'open' : ''}`}>
      <div className="accordion-header" onClick={() => setIsOpen(!isOpen)}>
        {isEditing ? (
          <input
            type="text"
            value={item.title}
            onChange={handleTitleChange}
            placeholder="Section title..."
            onClick={(e) => e.stopPropagation()}
            className="accordion-input"
          />
        ) : (
          <span>{item.title || 'Section Title'}</span>
        )}
        <div className="accordion-actions">
          {isEditing && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(index);
              }}
              className="accordion-delete-btn"
            >
              <FiTrash2 size={14} />
            </button>
          )}
          <FiChevronDown
            size={18}
            className={`accordion-icon ${isOpen ? 'open' : ''}`}
          />
        </div>
      </div>
      {isOpen && (
        <div className="accordion-content">
          {isEditing ? (
            <textarea
              value={item.content}
              onChange={handleContentChange}
              placeholder="Enter content..."
              className="accordion-textarea"
              rows={4}
            />
          ) : (
            <p>{item.content || 'Content goes here...'}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Accordion Node View Component
const AccordionNodeView = ({ node, updateAttributes, selected }) => {
  const [isEditing, setIsEditing] = useState(true);
  const items = node.attrs.items || [];

  const handleUpdateItem = (index, newItem) => {
    const newItems = [...items];
    newItems[index] = newItem;
    updateAttributes({ items: newItems });
  };

  const handleDeleteItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    updateAttributes({ items: newItems });
  };

  const handleAddItem = () => {
    updateAttributes({
      items: [...items, { title: '', content: '' }],
    });
  };

  return (
    <NodeViewWrapper className={`accordion-block ${selected ? 'selected' : ''}`}>
      <div className="accordion-block-header">
        <FiLayers size={16} />
        <span>Accordion</span>
        <button
          type="button"
          onClick={() => setIsEditing(!isEditing)}
          className="accordion-edit-toggle"
        >
          {isEditing ? 'Preview' : 'Edit'}
        </button>
      </div>
      <div className="accordion-items">
        {items.map((item, index) => (
          <AccordionItemComponent
            key={index}
            item={item}
            index={index}
            onUpdate={handleUpdateItem}
            onDelete={handleDeleteItem}
            isEditing={isEditing}
          />
        ))}
      </div>
      {isEditing && (
        <button type="button" onClick={handleAddItem} className="accordion-add-btn">
          <FiPlus size={16} />
          <span>Add Section</span>
        </button>
      )}
    </NodeViewWrapper>
  );
};

// Accordion Extension
export const Accordion = Node.create({
  name: 'accordion',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      items: {
        default: [
          { title: 'Section 1', content: 'Content for section 1' },
          { title: 'Section 2', content: 'Content for section 2' },
        ],
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="accordion"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'accordion' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AccordionNodeView);
  },

  addCommands() {
    return {
      insertAccordion:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              items: [
                { title: 'Section 1', content: 'Content for section 1' },
                { title: 'Section 2', content: 'Content for section 2' },
              ],
            },
          });
        },
    };
  },
});

export default Accordion;
